// What's new: the dialog after a release and the page listing every release so far.
// See docs/release-notes.md.
//
// `releases` holds, per backend release id (ReleaseNoteCatalog.RELEASES), a title and, per highlight
// id, a title and a text. Where a highlight's button leads lives in services/releaseNotes.js.
//
// A highlight may carry `details`, sparingly: only the few points somebody needs to know that the text
// does not already say, each a `term` as the screen shows it and one sentence (`text`). Usually without
// a section title; one (`title`) only where the points share something that makes them make sense. A
// simple change needs no details. Same ids as the German bundle. No curly braces, no vertical bar and
// no @: vue-i18n reads them as placeholder, plural and link.
export default {
  title: "What's new",
  subtitle: "What has changed in Lewara: new features and improvements",
  empty: "Nothing new yet",
  unseen: "New",
  contents: "Contents",
  count: "{n} change | {n} changes",
  // Whether the workspace's plan has the feature. On every card, including for employees who cannot
  // buy anything: they should know whether they already have it or somebody has to add it first.
  plan: {
    included: "Included in your plan",
    not_included: "Not included in your plan",
  },
  open: "Take a look",
  enlarge: "Enlarge image",
  shrink: "Close image",
  dialog: {
    eyebrow: "New in Lewara",
    title: "Here's what's new, {name}",
    title_anonymous: "Here's what's new",
    text: "Lewara has grown since your last visit. The highlights at a glance:",
    contents: "Contents",
    all: "All updates",
    confirm: "Got it",
    close: "Close what's new",
  },
  releases: {
    RELEASE_2026_09_28: {
      title: "Time tracking, service catalogue and much more",
      highlights: {
        TIME_TRACKING: {
          title: "Time tracking",
          text:
            "Clock in and out with one click and book the time straight to the project. The office reviews " +
            "and approves everybody's hours on one page, ready for payroll.",
          details: {
            links: {
              title: "Also found here",
              items: {
                settings: {
                  term: "Settings",
                  text: "Working-time models with weekly hours, break rules and shifts.",
                },
                employee: {
                  term: "Employees",
                  text: "Every employee gets a working-time model and an opening balance.",
                },
                project: {
                  term: "Project",
                  text: "The Times tab shows the hours clocked and checks them against the reports.",
                },
              },
            },
          },
        },
        SERVICE_CATALOG: {
          title: "Service catalogue instead of montage",
          text:
            "Your contract's bill of quantities now lives in the project, with the progress of every " +
            "position. In the [[cr]] you book the quantity done straight to its position.",
          details: {
            main: {
              items: {
                gaeb: {
                  term: "Import GAEB",
                  text: "Take over bills of quantities as X83, X84 or X86.",
                },
                change_orders: {
                  term: "Change orders",
                  text: "Only approved change orders count toward the contract value.",
                },
                migrated: {
                  term: "Existing montage lists",
                  text: "Have been moved into the service catalogue as a title of their own.",
                },
              },
            },
          },
        },
        PURCHASE_PRICES: {
          title: "Several purchase prices",
          text:
            "Store several purchase prices for machines and materials, say one per supplier, and pick the " +
            "right one when booking. The price is fixed with the report, so later changes do not shift the " +
            "post-calculation.",
          details: {
            main: {
              items: {
                project: {
                  term: "On the project",
                  text: "Every project has its own price list and shows under each price where the master data differs.",
                },
                migrated: {
                  term: "After the update",
                  text: "Your previous price is now the default price, and none of your figures change.",
                },
              },
            },
          },
        },
        MACHINE_UNITS: {
          title: "Machines by the hour or by the day",
          text:
            "Every machine is now billed per hour or per day, and its use is recorded in hours. That keeps " +
            "costs right even when a machine runs for only part of a shift.",
          details: {
            main: {
              items: {
                unit: {
                  term: "Billing unit",
                  text: "On the machine, a choice of hour or day instead of free text.",
                },
                figures: {
                  term: "Good to know",
                  text: "Machines billed by the hour now cost shift hours times price in [[cr.pl]], in older reports too.",
                },
              },
            },
          },
        },
        REPORTS: {
          title: "[[Cr.pl]] and [[hwr.pl]]",
          text:
            "[[Hwr.pl]] are now attached to the [[cr]], so the post-calculation does " +
            "not count the same work twice. Both tabs also show a calendar with your reports' print status.",
          details: {
            main: {
              items: {
                categories: {
                  term: "Categories",
                  text: "Pick or create them right in the [[hwr]].",
                },
                default_on: {
                  term: "Good to know",
                  text: "Attaching is on for every project. If the work in your [[hwr.pl]] comes on top, switch it off on the project's Configuration tab.",
                },
              },
            },
          },
        },
        BUDGET_HEALTH: {
          title: "Budget light by completion",
          text:
            "The light now compares the budget used with how complete the project is. So Lewara warns you " +
            "as soon as costs run ahead of the work, not only once the budget is spent.",
          details: {
            main: {
              items: {
                completion: {
                  term: "Completion",
                  text: "Comes from the service catalogue or is entered by hand in the budget dialog.",
                },
              },
            },
          },
        },
        PROJECT_LIST: {
          title: "New project list",
          text:
            "The project list has a new look and shows customer, town, period, leads and budget at a glance. " +
            "A right-click unfolds a row with its figures.",
        },
        PACKETS: {
          title: "Packets in the catalogue",
          text:
            "Packets now sit right in the lists of materials, machines and additional efforts. “Create " +
            "packet from this” turns any entry into a new packet.",
        },
        FILE_BROWSER: {
          title: "All files in one place",
          text:
            "Every file you have stored, whether on a project, a customer or a machine, is now under Files, " +
            "with one search across every folder. Certificates get an expiry date, and Lewara reminds you in " +
            "good time.",
        },
        ACTIVITY_LOG: {
          title: "Activity on every record",
          text:
            "Projects, customers, employees, master data and reports have an Activity tab. It shows who " +
            "changed what and when, and where the record is still in use.",
        },
        PERSONNEL_PLANNING: {
          title: "Personnel planning",
          text:
            "Overall planning shows every project in one calendar that picks up new projects by itself. " +
            "Capacity is now shown per phase, and zoom and full screen keep large plans clear.",
        },
      },
    },
    RELEASE_2026_07_23: {
      title: "Running post-calculation",
      highlights: {
        PROJECT_COSTS: {
          title: "Running post-calculation",
          text:
            "Track each project's contract value, booked cost and projected margin as work goes on " +
            "and get warned before a project slips into the red.",
        },
      },
    },
  },
}
